import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ThumbsUp, MessageCircle, Share2, Linkedin, Calendar, Users, Briefcase } from 'lucide-react';
import { Card } from '../components/ui/card';
import { linkedinPosts, companyInfo } from '../data/linkedinPosts';

export const LinkedInPage = () => {
  const [expandedPosts, setExpandedPosts] = useState({});

  const togglePost = (postId) => {
    setExpandedPosts(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Group posts by year
  const postsByYear = linkedinPosts.reduce((acc, post) => {
    const year = new Date(post.date).getFullYear();
    if (!acc[year]) acc[year] = [];
    acc[year].push(post);
    return acc;
  }, {});

  const years = Object.keys(postsByYear).sort((a, b) => b - a);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero Section */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/"
            className="inline-flex items-center text-slate-300 hover:text-emerald-400 mb-8 transition-colors duration-200 group"
          >
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
            Retour à l'accueil
          </Link>

          <div className="flex items-center space-x-4 mb-6">
            <div className="w-24 h-24 bg-white rounded-2xl flex items-center justify-center shadow-xl">
              <img src="/logo.svg" alt="EnovationWeb" className="w-20 h-20" />
            </div>
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold mb-2">{companyInfo.name}</h1>
              <p className="text-xl text-slate-300">{companyInfo.tagline}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8">
            <div className="flex items-center space-x-3">
              <Calendar className="w-6 h-6 text-emerald-400" />
              <div>
                <div className="text-sm text-slate-400">Fondée</div>
                <div className="font-semibold">{companyInfo.founded}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Users className="w-6 h-6 text-emerald-400" />
              <div>
                <div className="text-sm text-slate-400">Employés</div>
                <div className="font-semibold">{companyInfo.employees}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Briefcase className="w-6 h-6 text-emerald-400" />
              <div>
                <div className="text-sm text-slate-400">Industrie</div>
                <div className="font-semibold text-sm">{companyInfo.industry}</div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Linkedin className="w-6 h-6 text-emerald-400" />
              <div>
                <div className="text-sm text-slate-400">Posts</div>
                <div className="font-semibold">{linkedinPosts.length}</div>
              </div>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-sm font-semibold text-slate-400 mb-3">Spécialités</h3>
            <div className="flex flex-wrap gap-2">
              {companyInfo.specialties.map((specialty, index) => (
                <span 
                  key={index}
                  className="px-4 py-2 bg-emerald-600/20 border border-emerald-500/30 text-emerald-300 rounded-full text-sm font-medium"
                >
                  {specialty}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Posts Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Notre Parcours sur LinkedIn
            </h2>
            <p className="text-lg text-slate-600">
              Découvrez notre évolution depuis 2018 à travers nos publications
            </p>
          </div>

          {years.map(year => (
            <div key={year} className="mb-12">
              <div className="flex items-center mb-6">
                <div className="flex-shrink-0 w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-xl">{year}</span>
                </div>
                <div className="flex-grow h-1 bg-emerald-200 ml-4"></div>
              </div>

              <div className="space-y-6 ml-0 md:ml-24">
                {postsByYear[year]
                  .sort((a, b) => new Date(b.date) - new Date(a.date))
                  .map(post => {
                    const isExpanded = expandedPosts[post.id];
                    const contentPreview = post.content.substring(0, 200);
                    const shouldTruncate = post.content.length > 200;

                    return (
                      <Card 
                        key={post.id}
                        className="p-6 hover:shadow-xl transition-all duration-300 border-2 border-slate-100"
                      >
                        {/* Post Header */}
                        <div className="flex items-start space-x-4 mb-4">
                          <div className="w-12 h-12 bg-emerald-600 rounded-full flex items-center justify-center flex-shrink-0">
                            <Linkedin className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-900">{companyInfo.name}</h3>
                            <p className="text-sm text-slate-500">{formatDate(post.date)}</p>
                          </div>
                        </div>

                        {/* Post Content */}
                        <div className="mb-4">
                          <p className="text-slate-700 leading-relaxed whitespace-pre-line">
                            {isExpanded || !shouldTruncate ? post.content : `${contentPreview}...`}
                          </p>
                          {shouldTruncate && (
                            <button
                              onClick={() => togglePost(post.id)}
                              className="text-emerald-600 hover:text-emerald-700 font-medium text-sm mt-2 transition-colors duration-200"
                            >
                              {isExpanded ? 'Voir moins' : 'Voir plus'}
                            </button>
                          )}
                        </div>

                        {/* Post Stats */}
                        <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                          <div className="flex items-center space-x-6 text-sm text-slate-500">
                            <div className="flex items-center space-x-2 hover:text-emerald-600 transition-colors duration-200 cursor-pointer">
                              <ThumbsUp className="w-4 h-4" />
                              <span>{post.likes}</span>
                            </div>
                            <div className="flex items-center space-x-2 hover:text-emerald-600 transition-colors duration-200 cursor-pointer">
                              <MessageCircle className="w-4 h-4" />
                              <span>{post.comments}</span>
                            </div>
                            <div className="flex items-center space-x-2 hover:text-emerald-600 transition-colors duration-200 cursor-pointer">
                              <Share2 className="w-4 h-4" />
                              <span>{post.shares}</span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Linkedin className="w-16 h-16 mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Suivez-Nous sur LinkedIn
          </h2>
          <p className="text-xl mb-8 text-emerald-100">
            Restez connectés pour découvrir nos dernières actualités, projets et conseils digitaux !
          </p>
          <a 
            href="https://www.linkedin.com/company/enovationweb" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <button className="bg-white text-emerald-600 hover:bg-emerald-50 px-8 py-4 rounded-full font-semibold text-lg transition-all duration-200 transform hover:scale-105 shadow-lg">
              Suivre sur LinkedIn
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};
